import React from 'react';
import _merge from 'lodash/merge';
import { getContext } from 'common';
import ChevronRightIcon from '@nokia-csf-uxr/ccfk-assets/ChevronRightIcon';
import ChevronLeftIcon from '@nokia-csf-uxr/ccfk-assets/ChevronLeftIcon';
import IconButton, { IconButtonProps } from 'components/IconButton';

import Styled from './styles';

type MonthOnChange = (arg: { updateMonthWith: 1 | -1, event: React.MouseEvent | React.KeyboardEvent }) => void;

export interface CalendarMonthPickerProps extends Omit<React.ComponentPropsWithRef<'div'>, 'onChange'> {
  /**
   * The visible date's month-year. e.g.: "June 2021".
   *  Visible date is the same date that is visible on <YearPicker /> and <DayPicker/>.
   *  Read README tab for more information about `Visible date` and `Selected Date`. */
  visibleMonth?: React.ReactNode;
  /** To overwrite the next month button. */
  nextMonthButton?: React.ReactNode;
  /** To overwrite the previous month button. */
  preMonthButton?: React.ReactNode;
  /** Callback fired when next/pre buttons are clicked. It returns +1 when next month button is clicked or -1 when previous month button is clicked using `updateMonthWith`, `function({ event, updateMonthWith }) => void ` */
  onChange?: MonthOnChange;
  /** To overwrite the next month `<Button />` props. See Button component documentation for full details. */
  nextMonthButtonProps?: Partial<IconButtonProps>;
  /** To overwrite the previous month `<Button />` props. See Button component documentation for full details. */
  preMonthButtonProps?: Partial<IconButtonProps>;
  /** Set `true` to hide the next month button. It can be used in making range. */
  hideNextMonthButton?: boolean;
  /** Set `true` to hide the previous month button. It can be used in making range. */
  hidePreMonthButton?: boolean;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
}

/**
 * <CalendarMonthPicker />, any other props will be provided to the root element.
 * To get the ref of the outermost element use `ref` prop. [Read React useRef hook documentation](https://reactjs.org/docs/hooks-reference.
 */
const CalendarMonthPicker = React.forwardRef<HTMLElement, CalendarMonthPickerProps>((props: CalendarMonthPickerProps, ref) => getContext(({ RTL, theme }) => {
  const {
    onChange,
    visibleMonth,
    nextMonthButtonProps,
    preMonthButtonProps,
    nextMonthButton,
    preMonthButton,
    hideNextMonthButton,
    hidePreMonthButton,
    theme: themeProps,
    ...otherProps
  } = props;
  const resolvedTheme = _merge({}, theme ? theme.CALENDAR.MONTH_TITLE : {}, themeProps);
  const nextButtonProps = hideNextMonthButton ? { disabled: true, style: { opacity: 0, pointerEvents: 'none' as const } } : {};
  const preButtonProps = hidePreMonthButton ? { disabled: true, style: { opacity: 0, pointerEvents: 'none' as const } } : {};
  return (
    <Styled.CalendarMonthPicker
      $rtl={RTL}
      $theme={resolvedTheme}
      {...otherProps}
      re={ref}
    >
      {preMonthButton || (
        <IconButton
          {...preButtonProps}
          onClick={(event) => { onChange && onChange({ event, updateMonthWith: RTL ? 1 : -1 }); }}
          {...preMonthButtonProps}
        >
          <ChevronLeftIcon />
        </IconButton>
      )}
      {visibleMonth !== undefined && <Styled.CalendarMonthPickerTitle>{visibleMonth}</Styled.CalendarMonthPickerTitle>}
      {nextMonthButton || (
        <IconButton
          {...nextButtonProps}
          onClick={(event) => { onChange && onChange({ event, updateMonthWith: RTL ? -1 : 1 }); }}
          {...nextMonthButtonProps}
        >
          <ChevronRightIcon />
        </IconButton>
      )}
    </Styled.CalendarMonthPicker>
  );
}));

CalendarMonthPicker.defaultProps = {
  visibleMonth: undefined,
  nextMonthButton: undefined,
  preMonthButton: undefined,
  nextMonthButtonProps: {},
  preMonthButtonProps: {},
  onChange: undefined,
  hideNextMonthButton: false,
  hidePreMonthButton: false,
  theme: {}
};

CalendarMonthPicker.displayName = 'CalendarMonthPicker';

export default CalendarMonthPicker;
